<template>
  <Dialog show-close @close="handleClose" :align-bottom="mobile">
    <template v-slot:header>
      <H3>{{ title }}</H3>
    </template>
    <div class="image-upload-dialog__content">
      <ImageUploader
        @files="onFileUploaded"
        :max="multiple ? MAX_NUM_OF_IMAGES : 1"
        :enabled="multiple ? files.length < MAX_NUM_OF_IMAGES : files.length === 0"
        :compact="mobile"
      />
      <div v-if="!hasNoFiles" ref="imageGridRef" class="image-grid" :class="{ multiple: multiple, single: !multiple }">
        <div v-for="(file, index) in files" :key="getFileKey(file, index)" class="image-grid-item" :data-index="index">
          <img :src="filePreviewUrls[index]" :alt="file.name" />
          <span v-if="index === 0 && hasCoverImage" class="image-cover-text">Cover</span>
          <button @click="removeFile(index)"><i class="icon icon-close"></i></button>
        </div>
      </div>
      <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
    </div>

    <div class="drag-tip"><InfoIcon class="icon" /> Hold and drag to reorder.</div>

    <template v-slot:footer>
      <button class="footer-checkbox" @click="toggleConsent">
        <SquareCheck :checked="consentChecked" :disabled="hasNoFiles || loading" />
        <span
          >I confirm that I own this image or have the legal right to grant TourHero a commercial license to use it for
          marketing and selling this trip.<span class="required"
        /></span>
      </button>
      <div class="footer-buttons">
        <Button color="green" invert @click="handleClose" :disabled="loading">{{ cancelButtonText }}</Button>
        <Button color="blue" @click="handleSave" :disabled="!consentChecked || hasNoFiles || loading">
          <span>{{ confirmButtonText }} <SpinnerIcon v-if="loading" /></span>
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
import { Dialog, H3 } from '@/components/Theme'
import Button from '@/components/Theme2/Button.vue'
import ImageUploader from '../../LiteCMS/Common/AddImages/ImageUploader.vue'
import SquareCheck from '@/components/Theme/SquareCheck.vue'
import { useResponsive } from '@/helpers/vueHelpers.js'
import { useScrollLock } from '@vueuse/core'
import { Product } from 'app/javascript/src/models'
import SpinnerIcon from 'app/javascript/src/components/Theme/SpinnerIcon.vue'
import InfoIcon from 'app/assets/images/self-serve/info.svg?component'
import Sortable from 'sortablejs'

const { mobile } = useResponsive()

const MAX_NUM_OF_IMAGES = 20
const props = withDefaults(
  defineProps<{
    title: string
    confirmButtonText?: string
    cancelButtonText?: string
    multiple?: boolean
    closeAfterSave?: boolean
    images?: Product['itinerary']['images']
    hasCoverImage?: boolean
    loading?: boolean
  }>(),
  {
    multiple: false,
    confirmButtonText: 'Save',
    cancelButtonText: 'Cancel',
    closeAfterSave: false,
    images: () => [],
    hasCoverImage: false,
    loading: false,
  },
)
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', data: { files: File[]; imageSlugs: string[]; imageOrder: string[] }): void
}>()

const locked = useScrollLock(document)

const isFile = (item: any): item is File => {
  return item instanceof File
}

const consentChecked = ref(false)
const files = ref<(File | Product['itinerary']['images'][number])[]>([])
const filePreviewUrls = ref<string[]>([])
const imageGridRef = ref<HTMLElement | null>(null)
let sortableInstance: Sortable | null = null

const errorMessage = ref<string | null>(null)

const hasNoFiles = computed(() => files.value.length < 1)

const getFileKey = (file: File | Product['itinerary']['images'][number], _index: number): string => {
  if (isFile(file)) {
    return `file-${file.name}-${file.size}-${file.lastModified}`
  }
  return `existing-${file.slug}`
}

const initSortable = () => {
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
  if (imageGridRef.value && props.multiple && files.value.length > 1) {
    sortableInstance = Sortable.create(imageGridRef.value, {
      draggable: '.image-grid-item',
      animation: 150,
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      onEnd: (evt: Sortable.SortableEvent) => {
        const { oldIndex, newIndex } = evt
        if (oldIndex == null || newIndex == null || oldIndex === newIndex) return

        const movedFile = files.value.splice(oldIndex, 1)[0]
        files.value.splice(newIndex, 0, movedFile)

        const movedUrl = filePreviewUrls.value.splice(oldIndex, 1)[0]
        filePreviewUrls.value.splice(newIndex, 0, movedUrl)
      },
    })
  }
}

onMounted(() => {
  locked.value = true

  files.value = [...(props.images ?? [])]

  filePreviewUrls.value = files.value.map(item => {
    if (isFile(item)) {
      return URL.createObjectURL(item)
    } else {
      return item.thumbnail ?? item.url
    }
  })

  nextTick(() => initSortable())
})

watch(
  () => files.value.length,
  () => {
    nextTick(() => initSortable())
  },
)
onUnmounted(() => {
  locked.value = false
  if (sortableInstance) {
    sortableInstance.destroy()
    sortableInstance = null
  }
})
onBeforeUnmount(() => {
  filePreviewUrls.value.forEach(url => URL.revokeObjectURL(url))
})

const toggleConsent = () => {
  if (hasNoFiles.value) {
    return
  }
  consentChecked.value = !consentChecked.value
}

const handleClose = () => {
  if (props.loading) {
    return
  }
  emit('close')
  filePreviewUrls.value.forEach(url => URL.revokeObjectURL(url))
}

const onFileUploaded = (_files: File[]) => {
  errorMessage.value = null
  const max = props.multiple ? MAX_NUM_OF_IMAGES : 1
  const currentCount = files.value.length
  const remainingSlots = max - currentCount

  if (remainingSlots <= 0) {
    errorMessage.value = `You can't add more than ${max} images.`
    return
  }

  const filesToAdd = _files.slice(0, remainingSlots)
  files.value = [...files.value, ...filesToAdd]

  const newPreviewUrls = filesToAdd.map(file => URL.createObjectURL(file))
  filePreviewUrls.value = [...filePreviewUrls.value, ...newPreviewUrls]

  if (_files.length > remainingSlots) {
    const ignoredCount = _files.length - remainingSlots
    errorMessage.value = `${remainingSlots} file(s) added. ${ignoredCount} file(s) ignored due to maximum ${max} files allowed.`
  }

  // Reset consent checkbox when a new file is uploaded
  consentChecked.value = false
}

const removeFile = (index: number) => {
  errorMessage.value = null
  URL.revokeObjectURL(filePreviewUrls.value[index])
  files.value = files.value.filter((_, i) => i !== index)
  filePreviewUrls.value = filePreviewUrls.value.filter((_, i) => i !== index)

  if (files.value.length === 0) {
    consentChecked.value = false
  }
}

const handleSave = () => {
  if (!consentChecked.value || hasNoFiles.value) {
    return
  }

  const newFiles: File[] = []
  const imageSlugs: string[] = []
  const imageOrder: string[] = []
  let newFileIndex = 0

  files.value.forEach(item => {
    if (isFile(item)) {
      newFiles.push(item)
      imageOrder.push(`new:${newFileIndex}`)
      newFileIndex++
    } else {
      imageSlugs.push(item.slug)
      imageOrder.push(item.slug)
    }
  })

  emit('save', { files: newFiles, imageSlugs, imageOrder })

  if (props.closeAfterSave) {
    handleClose()
  }
}
</script>

<style lang="scss" scoped>
.image-upload-dialog__content {
  @include flex-column($spacing-4);

  .image-grid {
    &.multiple {
      display: grid;
      grid-template-columns: repeat(5, 1fr);

      @include mobile {
        grid-template-columns: repeat(3, 1fr);
      }
      gap: $spacing-3;

      img {
        width: 100%;
        aspect-ratio: 1;
        border-radius: $rounded-2xl;
      }
    }

    &.single {
      display: flex;
      justify-content: center;
      align-items: center;

      img {
        height: 250px;
        max-width: 100%;
        object-fit: cover;
        border-radius: $rounded-2xl;
      }
    }

    .image-cover-text {
      position: absolute;
      top: 8px;
      left: 8px;
      background: $grey-25;
      padding: $spacing-1 $spacing-2;
      font-size: $text-xs;
      border-radius: $rounded-xl;
    }

    .image-grid-item {
      display: flex;
      position: relative;
    }

    &.multiple .image-grid-item {
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }

    .sortable-ghost {
      opacity: 0.3;
    }

    .sortable-chosen {
      cursor: grabbing;
    }

    button {
      font-size: 10px;
      line-height: 1;
      position: absolute;
      top: 8px;
      right: 8px;
      color: $black;
      background: $grey-25;
      border-radius: 50%;
      padding: 7px;
    }
  }

  .error-message {
    color: $red;
    font-size: $text-sm;
    text-align: center;
  }
}

.drag-tip {
  .icon {
    width: 12px;
  }

  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 6px;
  color: $grey-100;
  font-size: 14px;
  line-height: 16px;
}

.theme-dialog {
  :deep(.modal__container) {
    @include hide-scrollbar;
    max-height: 90vh;
    max-width: $breakpoint-m;
  }

  :deep(.modal__footer) {
    display: flex;
    flex-direction: column;
    gap: $spacing-4;

    @include mobile {
      gap: $spacing-4;
    }

    .footer-checkbox {
      display: flex;
      justify-content: center;
      gap: $spacing-2;
      cursor: pointer;
      text-align: left;
      color: $black;

      .required::after {
        content: '*';
        color: $brand-secondary;
      }
    }

    .footer-buttons {
      display: flex;
      justify-content: center;
      gap: $spacing-4;

      @include mobile {
        flex-direction: column-reverse;
        gap: $spacing-2;
      }
    }
  }
}
</style>
